import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useEditor } from '@craftjs/core';
import { TextField as InspectorTextField } from '@/components/inspector-fields/TextField/TextField';
import { useFormFieldHiddenSync } from '@/hooks/useFormFieldHiddenSync';
import { TextAreaField as InspectorTextAreaField } from '@/components/inspector-fields/TextAreaField/TextAreaField';
import IconClose from '@/assets/icons/icon-close.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { Switch } from '@/components/inspector-fields/Switch/Switch';
import { Textarea } from '@/components/ui/textarea';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import IconPlus from '@/assets/icons/icon-plus.svg?react';
import {
  DraggableOptionList,
  type OptionItem,
} from '@/components/inspector-fields/OptionField/DraggableOptionList';
import type { FormSelectionFieldProps } from '@builder4/shared/components/Form';
import { generateUuidV4 } from '@builder4/shared/components/Form/form-utils';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { FORM_SELECTION_FIELD_NODE_NAME, FIELD_LABEL_SELECTION } from '@/utils/constants';

export interface SelectionFieldProps {
  className?: string;
  title?: string;
  fieldNodeId: string;
  onClose?: () => void;
  getDefaultFieldLabel: () => string;
}

export const FormSelectionField: React.FC<SelectionFieldProps> = ({
  className = '',
  title = 'Selection',
  fieldNodeId,
  onClose,
  getDefaultFieldLabel,
}) => {
  const { actions, fieldProps } = useEditor((_, query) => {
    const node = query.node(fieldNodeId).get();
    return {
      fieldProps: (node?.data.props || {}) as FormSelectionFieldProps,
    };
  });

  const { setBPProp, getBPProp } = useBreakpointAwareProp(FORM_SELECTION_FIELD_NODE_NAME);

  // Read props using breakpoint-aware getter
  const placeholder = getBPProp(fieldProps, 'placeholder');
  const showHelpText = getBPProp(fieldProps, 'showHelpText');
  const helpText = getBPProp(fieldProps, 'helpText');
  const required = getBPProp(fieldProps, 'required');
  const hidden = getBPProp(fieldProps, 'hidden');
  const nodeOptions = getBPProp(fieldProps, 'options');
  const nodeDefaultIndices = getBPProp(fieldProps, 'defaultIndices');
  const isSingleSelect = getBPProp(fieldProps, 'isSingleSelect');

  // Local state for options management (string array) - initialized from breakpoint-aware props
  const [options, setOptions] = useState<string[]>(() => nodeOptions || []);
  // Use index-based storage to handle duplicate option names correctly (always multi-select)
  const [defaultIndices, setDefaultIndices] = useState<number[]>(() => nodeDefaultIndices || []);

  // Adapter functions to work with DraggableOptionList (which uses OptionItem[])
  const optionsToItems = (opts: string[]): OptionItem[] =>
    opts.map((text, index) => ({ id: String(index), text }));

  const itemsToOptions = (items: OptionItem[]): string[] => items.map((item) => item.text);

  // Get default ID for single-select mode
  const getDefaultId = (): string | undefined => {
    return defaultIndices.length > 0 ? String(defaultIndices[0]) : undefined;
  };

  // Get all default IDs for multi-select mode
  const getDefaultIds = (): string[] => {
    return defaultIndices.map((idx) => String(idx));
  };

  const label = getBPProp(fieldProps, 'label');

  // Sync local state to CraftJS node — ignore() prevents undo loops where
  // reverting this entry triggers a reverse sync that re-creates the entry.
  useEffect(() => {
    actions.history.ignore().setProp(fieldNodeId, (props: FormSelectionFieldProps) => {
      setBPProp(props, 'options', options);
      setBPProp(props, 'defaultIndices', defaultIndices);
    });
  }, [options, defaultIndices, fieldNodeId, actions, setBPProp]);

  // Sync breakpoint-aware props to local state when they change
  // Use comparison to avoid infinite loops from circular updates
  useEffect(() => {
    const newOptions = nodeOptions || [];
    setOptions((prev) => {
      if (prev.length === newOptions.length && prev.every((val, idx) => val === newOptions[idx])) {
        return prev; // No change, return same reference
      }
      return newOptions;
    });
  }, [nodeOptions]);

  // Sync CraftJS defaultIndices to local state, with comparison to avoid infinite loops
  useEffect(() => {
    const newIndices = nodeDefaultIndices || [];
    setDefaultIndices((prev) => {
      if (prev.length === newIndices.length && prev.every((val, idx) => val === newIndices[idx])) {
        return prev; // No change, return same reference
      }
      return newIndices;
    });
  }, [nodeDefaultIndices]);

  // Write options/defaultIndices to CraftJS with history tracking
  const commitOptions = useCallback(
    (newOptions: string[], newDefaultIndices: number[]) => {
      setOptions(newOptions);
      setDefaultIndices(newDefaultIndices);
      actions.setProp(fieldNodeId, (props: FormSelectionFieldProps) => {
        setBPProp(props, 'options', newOptions);
        setBPProp(props, 'defaultIndices', newDefaultIndices);
      });
    },
    [actions, fieldNodeId, setBPProp],
  );

  // Handlers (working with string arrays - always multi-select)
  const handleReorder = useCallback(
    (next: OptionItem[]) => {
      // Build a mapping from old index to new index
      const oldToNewIndex = new Map<number, number>();
      next.forEach((item, newIndex) => {
        const oldIndex = parseInt(item.id, 10);
        oldToNewIndex.set(oldIndex, newIndex);
      });

      const newOptions = itemsToOptions(next);
      const newDefaults = defaultIndices
        .map((idx) => oldToNewIndex.get(idx) ?? idx)
        .filter((idx) => idx < newOptions.length);
      commitOptions(newOptions, newDefaults);
    },
    [defaultIndices, commitOptions],
  );

  const handleAddOption = useCallback(() => {
    if (options.length >= 50) return; // Maximum 50 options
    commitOptions([...options, 'New Option'], defaultIndices);
  }, [options, defaultIndices, commitOptions]);

  const handleSetDefault = useCallback(
    (id: string) => {
      const index = parseInt(id, 10);
      if (index >= 0 && index < options.length) {
        let newDefaults: number[];
        if (isSingleSelect) {
          // Single-select: toggle - if already default, unset; otherwise set as new default
          newDefaults = defaultIndices.length > 0 && defaultIndices[0] === index ? [] : [index];
        } else {
          // Multi-select: toggle in array
          newDefaults = defaultIndices.includes(index)
            ? defaultIndices.filter((i) => i !== index)
            : [...defaultIndices, index];
        }
        commitOptions(options, newDefaults);
      }
    },
    [options, defaultIndices, isSingleSelect, commitOptions],
  );

  const handleDuplicate = useCallback(
    (id: string) => {
      const index = parseInt(id, 10);
      if (index < 0 || index >= options.length || options.length >= 50) return;

      const newOptions = [
        ...options.slice(0, index + 1),
        options[index], // Duplicate the value
        ...options.slice(index + 1),
      ];
      // Shift default indices if they come after the duplicated position
      const newDefaults = defaultIndices.map((i) => (i > index ? i + 1 : i));
      commitOptions(newOptions, newDefaults);
    },
    [options, defaultIndices, commitOptions],
  );

  const handleDelete = useCallback(
    (id: string) => {
      if (options.length <= 2) return; // Minimum 2 options
      const index = parseInt(id, 10);
      if (index < 0 || index >= options.length) return;

      const newOptions = options.filter((_, i) => i !== index);
      // Update default indices: remove deleted index and shift remaining
      const newDefaults = defaultIndices
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i));
      commitOptions(newOptions, newDefaults);
    },
    [options, defaultIndices, commitOptions],
  );

  const handleEdit = useCallback(
    (id: string, text: string) => {
      if (!text.trim()) return; // Option text cannot be blank
      const index = parseInt(id, 10);
      if (index < 0 || index >= options.length) return;

      const newOptions = options.map((opt, i) => (i === index ? text : opt));
      commitOptions(newOptions, defaultIndices);
    },
    [options, defaultIndices, commitOptions],
  );

  // Throttled — batches rapid keystrokes (text inputs)
  const setProp = (callback: (props: FormSelectionFieldProps) => void) => {
    actions.history.throttle(500).setProp(fieldNodeId, callback);
  };

  // Direct — discrete actions (toggles, onBlur)
  const setPropDirect = (callback: (props: FormSelectionFieldProps) => void) => {
    actions.setProp(fieldNodeId, callback);
  };

  // Sync hidden prop with layer visibility
  const { handleHiddenChange } = useFormFieldHiddenSync<FormSelectionFieldProps>(
    fieldNodeId,
    hidden,
    setPropDirect,
  );

  useEffect(() => {
    actions.history.ignore().setCustom(fieldNodeId, (custom) => {
      custom.displayName = label || FIELD_LABEL_SELECTION;
    });
  }, [label, actions, fieldNodeId]);

  return (
    <div
      className={clsx(
        'bg-neutral-0 inline-flex h-[480px] max-h-[480px] w-[248px] flex-col items-center overflow-hidden rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline-[0.8px] outline-neutral-100',
        className,
      )}
      data-craft-history
    >
      {/* Card header */}
      <div className="bg-neutral-0 inline-flex h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
        <div className="text-xs leading-5 font-semibold text-neutral-900">{title}</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col border border-t-0 border-neutral-100">
        <div className="no-scrollbar flex-1 overflow-y-auto">
          <div className="flex w-full flex-col items-start justify-start gap-3 px-3 pt-3 pb-4">
            {/* Field Title - syncs with Layers panel */}
            <InspectorTextField
              className="w-full"
              classNameInputParent="max-w-none"
              classNameInput="max-w-[140px]"
              text="Field Label"
              inputValue={label}
              placeholderText="Selection"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setProp((props: FormSelectionFieldProps) => {
                  setBPProp(props, 'label', e.target.value);
                })
              }
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                if (!e.target.value) {
                  setProp((props: FormSelectionFieldProps) => {
                    setBPProp(props, 'label', getDefaultFieldLabel());
                  });
                }
              }}
            />

            {/* Label toggle with description textarea */}
            <Switch
              label="Description"
              checked={!!placeholder}
              onCheckedChange={(checked: boolean) => {
                if (!checked) {
                  setPropDirect((props: FormSelectionFieldProps) => {
                    setBPProp(props, 'placeholder', '');
                  });
                } else {
                  setPropDirect((props: FormSelectionFieldProps) => {
                    setBPProp(props, 'placeholder', 'Select one or more options:');
                  });
                }
              }}
            >
              <InspectorTextAreaField
                className="w-full"
                inputValue={placeholder}
                placeholderText="Select one or more options:"
                rows={2}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setProp((props: FormSelectionFieldProps) => {
                    setBPProp(props, 'placeholder', e.target.value);
                  })
                }
              />
            </Switch>

            {/* Single selection toggle (inverted: checked = single select / radio buttons) */}
            <Switch
              label="Single selection"
              checked={isSingleSelect}
              onCheckedChange={(checked: boolean) => {
                if (checked && defaultIndices.length > 1) {
                  // Switching to single-select with multiple defaults: keep only first
                  setDefaultIndices([defaultIndices[0]]);
                }
                // Hydra requires a new field ID when the field kind changes (single vs multi select).
                const newId = generateUuidV4();
                // Update CraftJS props
                setPropDirect((props: FormSelectionFieldProps) => {
                  setBPProp(props, 'isSingleSelect', checked);
                  props.id = newId;
                  if (checked && defaultIndices.length > 1) {
                    setBPProp(props, 'defaultIndices', [defaultIndices[0]]);
                  }
                });
              }}
            />

            {/* Divider before Options */}
            <div className="self-stretch pb-1">
              <div className="h-0 border-t border-neutral-100" />
            </div>

            {/* Options Management */}
            <div className="flex w-full flex-col gap-2">
              <div className="text-button-sm leading-4 font-medium text-neutral-500">Options</div>

              <DraggableOptionList
                items={optionsToItems(options)}
                defaultId={isSingleSelect ? getDefaultId() : undefined}
                defaultIds={!isSingleSelect ? getDefaultIds() : undefined}
                isMultiSelect={!isSingleSelect}
                onChange={handleReorder}
                onSetDefault={handleSetDefault}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                onEdit={handleEdit}
                disableDelete={() => options.length <= 2}
              />

              {/* Add Option Button */}
              {options.length < 50 && (
                <div className="flex items-center justify-between">
                  <span className="text-button-sm text-neutral-500">New Option</span>
                  <SecondaryButton size="sm" icon={IconPlus} text="Add" onClick={handleAddOption} />
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="self-stretch pb-1">
              <div className="h-0 border-t border-neutral-100" />
            </div>

            {/* Help Text Toggle */}
            <Switch
              label="Include help text"
              checked={showHelpText}
              onCheckedChange={(checked: boolean) =>
                setPropDirect((props: FormSelectionFieldProps) => {
                  setBPProp(props, 'showHelpText', checked);
                })
              }
            >
              <Textarea
                className="h-[109px] w-full overflow-hidden"
                placeholder="Add help text"
                value={helpText}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                  setProp((props: FormSelectionFieldProps) => {
                    setBPProp(props, 'helpText', e.target.value);
                  })
                }
              />
            </Switch>

            {/* Divider */}
            <div className="self-stretch pb-1">
              <div className="h-0 border-t border-neutral-100" />
            </div>

            {/* Other toggles */}
            <div className="flex w-full flex-col">
              <Switch
                label="This field is required"
                checked={required}
                onCheckedChange={(checked: boolean) =>
                  setPropDirect((props: FormSelectionFieldProps) => {
                    setBPProp(props, 'required', checked);
                  })
                }
              />
              <Switch
                label="This is a hidden field"
                checked={hidden}
                onCheckedChange={handleHiddenChange}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormSelectionField;
